import { DatePipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { Booking } from 'src/app/interfaces/booking';
import { tour } from 'src/app/interfaces/tour';
import { TicketInputComponent } from '../ticket-input/ticket-input.component';
import { TourService } from 'src/app/services/tour.service';
import { LoadscreenComponent } from '../loadscreen/loadscreen.component';
import { feedbackMessage } from 'src/app/interfaces/feedback-message';
import { Router } from '@angular/router';
import { ApiResponse } from 'src/app/interfaces/api-response';

@Component({
  selector: 'app-booking-summary',
  templateUrl: './booking-summary.component.html',
  styleUrls: ['./booking-summary.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    DatePipe,
    FormsModule,
    TicketInputComponent,
    LoadscreenComponent
  ]
})
export class BookingSummaryComponent {

  private modalController = inject(ModalController)
  private tourService = inject(TourService)
  public router = inject(Router)

  @Input() public tour: tour;
  @Input() public booking: Booking;
  @Input() public showControls: boolean = true;

  public isSubmitting: boolean = false;
  public feedbackMessage: feedbackMessage;
  public commissionRate: number = 0;


  /**
   * Processes a tour booking request by submitting the booking data to the tour service.
   * Validates that a booking exists and contains tickets before proceeding.
   * Sets isSubmitting flag during the API call and updates the feedbackMessage 
   * with success/error status when complete.
   * 
   * @returns void Returns early if booking validation fails
   */
  public bookTour() {
    if (!this.booking || this.booking.tickets.length === 0) {
      return;
    }

    this.isSubmitting = true;

    this.tourService.bookTour(this.booking).subscribe({
      next: (response: ApiResponse) => {
        this.isSubmitting = false;
        this.feedbackMessage = {
          status: 'success',
          icon: 'checkmark-circle',
          title: 'Booking gennemført',
          message: response['message']
        };
      },
      error: (error) => {
        console.error('Booking error', error);
        this.isSubmitting = false;
        this.feedbackMessage = {
          status: 'danger',
          icon: 'alert-circle-outline',
          title: 'Booking blev ikke gennemført',
          message: error['message']
        };
      }
    });
  }


  /**
   * Closes the current modal window by dismissing it with a 'dismissed' status.
   * This method is used to close the booking summary modal when the user wants to exit
   * or cancel the booking process.
   * @returns Promise<boolean> Returns a promise that resolves when the modal is closed
   */
  public closeModal() {
    this.modalController.dismiss({
      'dismissed': true
    })
  }


  /**
   * Closes the current modal window and navigates the user back to the tour consultant overview page.
   * This method is typically called after a successful booking to return the user to the main tour listing.
   */
  public closeModalAndRedirect() {
    this.closeModal();
    this.router.navigateByUrl('/tour-consultant/');
  }


  /**
   * Updates the commission rate in the booking data when the user changes the input value.
   * Converts the percentage input to a decimal value and stores it in the booking's commission field.
   * 
   * Author: Cursor
   * @param event The ion-input event containing the new commission rate value
   */
  public updateCommission(event: any) {
    const value = parseFloat(event.detail.value) || 0;
    this.commissionRate = value;
    
    // Update the booking commission field with the percentage value
    if (this.booking) {
      this.booking.commission = value;
    }
  }


}
